import {
  applyDiscount, dealStrikethrough,
  deviceDealTiers, deviceListPricing, deviceTermIsPriced, showsOneTime,
  buildPricePhases, phaseDisclosure, phaseHeadline,
  offerAmounts, offerShowsOneTime,
  dealPackageRows as sharedDealPackageRows, dealTotalsFromRows,
  packagePriceMap, PACKAGE_SHAPE, deviceShape, packagePhasedLines, dealPhaseView,
} from '@platform/shared-types';
import type {
  DealDeviceTerm, DealPackagesTotals,
  PriceIntro, PhasedLine, PricePhase, DealOfferTerm,
  DealDocuments, DealDocumentLine, DealDocumentSectionConfig,
  PackageRecordLike, OfferInputParsers,
  DealPackageRow as SharedDealPackageRow,
} from '@platform/shared-types';
import { money } from '../components/finance/financeFormat';
import {
  discountInputError, deviceTermError, dollarsToCents, toDiscount, toDeviceTerm,
  priceIntroError, toPriceIntro,
  offerTermError, toOfferTerm, offerTermToInput,
  type BillingSkuRow, type DeviceTermInput, type DiscountInput, type PlanDiscount,
  type PriceIntroInput, type OfferTermInput,
} from './billingApi';

/**
 * THE DEAL BUILDER'S ARITHMETIC, AND THE REQUEST BODY IT SAVES.
 *
 * Kept out of `DealsPage.tsx` for one reason: this repo can run plain vitest
 * but has no DOM renderer, so anything left inside a component is untestable.
 * Every number and every user-facing decision on that screen is decided here.
 *
 * ---------------------------------------------------------------------------
 * ONE SUM, NOT TWO
 * ---------------------------------------------------------------------------
 *
 * Every price on this screen comes from `offerAmounts` in
 * `@platform/shared-types` — the SAME pure function `super-admin-ext` and
 * `billing-service` price from. This module does not re-implement it and must
 * never grow a second copy: the builder's running total and the figure the
 * server hands back afterwards have to be the same number, or an operator
 * prices a deal against arithmetic that nothing downstream agrees with.
 *
 * The packages total (`dealTotalsFromRows`) is the SUM OF THE ROWS ABOVE IT
 * rather than a second walk over the same ids, so the tile and the rows cannot
 * disagree even in principle. It replaced a call to `dealPackagesTotals` that
 * read the retired `packageDiscounts` field: once the builder moved to the
 * unified offer, that total stopped moving at all — the Discount tile read
 * −$0.00 and the deal price stayed at list while every row showed a discount.
 *
 * ---------------------------------------------------------------------------
 * WHAT `priceCents` IS, AND IS NOT
 * ---------------------------------------------------------------------------
 *
 * `priceCents` is the deal's OWN price and is authoritative at signup no matter
 * what its packages sum to (spec §12/A2). So the two numbers are shown side by
 * side and their difference is stated plainly — nothing here silently corrects
 * one to the other.
 *
 * It is also a SINGLE-LOCATION figure: the monthly price for the tenant's first
 * location. Further locations price through `location-billing-service`, which is
 * untouched by this work. It must never be presented as a multi-store total.
 */

// ---------------------------------------------------------------------------
// The stored shapes — ONE definition, shared by the list page (DealsPage) and
// the routed editor (DealEditPage) so the two can never describe a deal
// differently.
// ---------------------------------------------------------------------------

export interface Deal {
  id: string;
  code: string;
  name: string;
  /** @deprecated superseded by `priceCents` — never removed; legacy deals carry only this. */
  stripePriceId?: string;
  /** @deprecated superseded by `priceCents`. Read through `effectiveDealPriceCents`, never directly. */
  monthlyAmount: number;
  trialDays: number;
  commissionMode: 'flat' | 'percentage' | 'both';
  flatFee?: number;
  percentageRate?: number;
  showOnLandingPage: boolean;
  landingPageLabel?: string;
  landingPageDescription?: string;
  isDefaultDeal: boolean;
  maxUses?: number;
  usedCount: number;
  expiresAt?: number;
  isActive: string;
  productId?: string;
  packageIds?: string[];
  deviceSkuIds?: string[];
  /** Same list as `deviceSkuIds`, under this task's name. The API keeps them in lockstep. */
  devicePackageIds?: string[];
  /** packageId -> the discount given on that package. ADMIN-ONLY. */
  packageDiscounts?: Record<string, PlanDiscount>;
  /** The deal's own monthly price, in integer cents. Authoritative at signup. */
  priceCents?: number;
  /** Server-computed, on the admin GET/POST/PUT responses only. */
  packagesTotals?: DealPackagesTotalsView;
  /** Who created this deal. ADMIN-ONLY (2026-08-24 spec §3.4). */
  createdBy?: string;
  /** Who most recently changed this deal's pricing. Same visibility as createdBy. */
  updatedBy?: string;
  /** Append-only pricing/package audit trail. Newest entry LAST — the same order it is stored in. */
  pricingHistory?: Array<{
    at: number;
    by: string;
    priceCents?: number;
    listCentsAtChange?: number;
    packageIds?: string[];
  }>;
  affiliateCount: number;
  createdAt: number;
  updatedAt: number;
}

export interface PackageItem {
  id: string;
  name: string;
  description?: string;
  productType?: string;
  category?: string;
  status?: string;
  features?: string[];
  /** List price in integer cents, from the packages table. */
  basePrice?: number;
  /**
   * Identity fields the packages page paints a card with — carried here so the
   * deal editor can draw the same package the same way instead of a plain row.
   * All optional: a package saved before these existed simply falls back to the
   * default accent.
   */
  color?: string;
  colorType?: 'solid' | 'gradient' | string;
  gradientFrom?: string;
  gradientTo?: string;
  icon?: string;
  /**
   * Which product line this package belongs to (2026-10-08). Missing ⇒
   * 'rubytill' — mirrors `@platform/shared-types` `packages.ts`. Carried here,
   * rather than read from shared-types, only to gate the Ruby Estate fee-plan
   * fields below on the deal editor: a deal shows them only when it includes
   * at least one package whose `productLine === 'rubyestate'`, never from a
   * hardcoded package-id list (CLAUDE.md "product line is admin data").
   */
  productLine?: string;
}

export interface Product {
  id: string;
  name: string;
  description?: string;
  status?: string;
  /** The packages a deal on this product includes — the set the API stores when none is sent. */
  defaultPackages?: string[];
}

export interface DealStats {
  totalDeals: number;
  activeDeals: number;
  inactiveDeals: number;
  totalUsedCount: number;
  affiliatesUsingDeals: number;
}

// ---------------------------------------------------------------------------
// Reading a stored deal
// ---------------------------------------------------------------------------

/**
 * The price a stored deal actually offers — `priceCents` when it has one,
 * otherwise the deprecated `monthlyAmount`.
 *
 * The SAME precedence as `super-admin-ext`'s `effectiveMonthlyAmountCents`, and
 * it has to be: the two fields are independently writable and DO disagree in
 * storage (an update that sends `monthlyAmount` alone leaves `priceCents`
 * untouched). Seeding this form from the wrong one of the pair would show an
 * operator a price nothing else in the platform agrees with. `getDeal` returns
 * the row RAW — unlike `listDeals`, it does not fold the two together — so this
 * is not optional on the read path.
 *
 * `priceCents: 0` wins over a positive `monthlyAmount`: a deal deliberately
 * priced at zero is a real deal, not a missing value.
 */
export function effectiveDealPriceCents(deal?: { monthlyAmount?: number; priceCents?: number } | null): number {
  if (typeof deal?.priceCents === 'number') return deal.priceCents;
  if (typeof deal?.monthlyAmount === 'number') return deal.monthlyAmount;
  return 0;
}

// ---------------------------------------------------------------------------
// Packages
// ---------------------------------------------------------------------------

/**
 * THE PACKAGE ARITHMETIC NOW LIVES IN `@platform/shared-types`.
 *
 * `packagePriceMap`, `PACKAGE_SHAPE`, `deviceShape`, `dealPackageRows`,
 * `packagePhasedLines`, `dealPhaseView` and `dealTotalsFromRows` moved to
 * `offerPresentation.ts` verbatim so a Lambda can reach them — the public
 * landing page and the deal live view have to compute the same numbers this
 * builder does, and a browser-only module cannot serve them.
 *
 * They are re-exported from here unchanged, so every existing caller and every
 * existing test in this repo is untouched.
 *
 * ONE SEAM. The shared `dealPackageRows` cannot call this repo's FORM parsers
 * (`toOfferTerm` and friends parse typed strings; a Lambda has no form), so it
 * takes them as an optional argument. `dealPackageRows` below binds this repo's
 * parsers into that argument, which is exactly what the moved body did inline —
 * so the function keeps its signature, its types and its behaviour to the cent.
 */
export {
  packagePriceMap, PACKAGE_SHAPE, deviceShape,
  packagePhasedLines, dealPhaseView, dealTotalsFromRows,
};
export type { PackageRecordLike, DealPhaseView } from '@platform/shared-types';

/**
 * A package row as THIS repo holds it: the three caller-shaped fields are the
 * editor's typed form inputs. The shared interface types them `any` because the
 * server holds already-parsed stored shapes in the same slots; narrowing them
 * back here keeps `row.offer`, `row.discount` and `row.intro` fully typed for
 * the editor's controls.
 */
export interface DealPackageRow extends SharedDealPackageRow {
  discount: DiscountInput;
  intro?: PriceIntroInput;
  offer: OfferTermInput;
}

/**
 * This repo's form parsers, as the shared row builder's seam.
 *
 * EXPORTED because the deal live view's model needs the same seam: it calls
 * `buildOfferPresentation` directly rather than `dealPackageRows`, and the
 * shared defaults read ALREADY-PARSED stored shapes. Handed nothing, they
 * reject the editor's typed `{ mode: 'percent', value: '33' }` — so a deal
 * carrying only the legacy `packageDiscounts` would preview at LIST while the
 * editor beside it showed the discount. There must be exactly one of these.
 */
export const FORM_PARSERS: OfferInputParsers = {
  parseTerm: (term, shape) => toOfferTerm(term, shape),
  termError: (term, shape) => offerTermError(term, shape),
  parseDiscount: (input) => toDiscount(input) as any,
  discountError: (input) => discountInputError(input),
  parseIntro: (input) => toPriceIntro(input) as any,
  introError: (input) => priceIntroError(input),
  termToInput: (term) => offerTermToInput(term),
};

/**
 * One row per package on the deal: list price, the discount as typed, and the
 * net that discount produces.
 *
 * The net is `offerAmounts`' settled monthly — the one pipeline the server bills
 * from — so a row and the total below it can never round apart. A discount the
 * server would refuse yields `undefined` from `toDiscount`, so the row bills at
 * LIST and `error` says why; it is never quietly rounded into a discount nobody
 * typed.
 */
export function dealPackageRows(
  packageIds: string[],
  packages: PackageRecordLike[],
  discounts: Record<string, DiscountInput>,
  /** Introductory prices, as typed. Absent for a deal with none. */
  intros?: Record<string, PriceIntroInput>,
  /** The unified offers, as typed. Preferred over `discounts`/`intros` when present. */
  offers?: Record<string, OfferTermInput>,
): DealPackageRow[] {
  return sharedDealPackageRows(
    packageIds, packages, discounts, intros, offers, FORM_PARSERS,
  ) as DealPackageRow[];
}

/** `packagesTotals`, as the admin endpoints return it, plus their load-failure flag. */
export interface DealPackagesTotalsView extends DealPackagesTotals {
  /**
   * The SERVER could not load the package prices. NOT the same as a deal that
   * legitimately sums to zero — the totals alongside it are meaningless and
   * must never be rendered as $0.00.
   */
  pricingUnavailable?: boolean;
}

/**
 * The builder's live total for the deal currently on screen, through the shared
 * `dealPackagesTotals`. `pricingUnavailable` is deliberately NOT set here: this
 * total is computed from the packages catalog the page already loaded, so if
 * that load had failed the page would have failed with it. The flag belongs to
 * the server's own answer — see `serverTotalsProblem`.
 */
export function liveDealTotals(
  packageIds: string[],
  packages: PackageRecordLike[],
  discounts: Record<string, DiscountInput>,
  /** The unified offers, as typed. Preferred over `discounts` when present. */
  offers?: Record<string, OfferTermInput>,
): DealPackagesTotals {
  return dealTotalsFromRows(dealPackageRows(packageIds, packages, discounts, undefined, offers));
}

/**
 * Why "Match the packages" must not offer its figure — or null when it may.
 *
 * The button copies the running package total into the deal price, so it may
 * only be offered when that total is a number the operator would actually mean
 * to charge. A discount the server would REFUSE is dropped by `toDiscount`, so
 * the row bills at list and the total is the price WITHOUT that discount:
 * offering it as "Match the packages ($X)" quotes a figure computed as though
 * an invalid discount did not exist. Save is already refused in that state;
 * this stops the button from naming a number in the meantime.
 */
export function matchPackagesBlock(
  rows: DealPackageRow[],
  totals: Pick<DealPackagesTotals, 'missingPackageIds'>,
): string | null {
  if (!rows.length) return 'This deal has no packages, so there is no figure to copy.';
  if (totals.missingPackageIds?.length) {
    return `${totals.missingPackageIds.length} package(s) have no catalog entry (${totals.missingPackageIds.join(', ')}), so the total understates the deal — copying it would turn a data gap into a price.`;
  }
  const bad = rows.find((r) => r.error);
  if (bad) {
    return `The discount on ${bad.name} is one the server would refuse (${bad.error}), so it is not in the total — the figure offered here would be the price WITHOUT it. Fix the discount first.`;
  }
  return null;
}

/**
 * What is wrong with the SERVER's `packagesTotals`, or null when nothing is.
 *
 * Returned as prose because there is nothing an operator can do about either
 * case from this screen except know about it — and `pricingUnavailable`
 * rendered as `$0.00` is the specific lie this exists to prevent.
 */
export function serverTotalsProblem(totals?: DealPackagesTotalsView | null): string | null {
  if (!totals) return null;
  if (totals.pricingUnavailable) {
    return 'billing could not load the package prices, so the deal’s package total is unknown — it is not zero.';
  }
  // REMOVED 2026-09-04: the stored-but-missing package list.
  //
  // It named ids the FORM has already dropped — the seed filters `packageIds`
  // against the catalog, and so does `packagesForProduct` — so the deal on
  // screen neither shows nor prices them, and the next save removes them. In
  // prod that produced a 16-id paragraph of amber text under a total that was
  // already correct, describing a discrepancy an operator could neither see nor
  // act on. The LIVE missing list (`liveTotals.missingPackageIds`) still shows,
  // because that one is about packages the deal is being priced with right now.
  return null;
}

// ---------------------------------------------------------------------------
// STEP 4 — the gap between the deal's price and the sum of its parts
// ---------------------------------------------------------------------------

export type DealPriceGapKind = 'no-price' | 'no-packages' | 'unpriceable' | 'match' | 'below' | 'above';

export interface DealPriceGap {
  kind: DealPriceGapKind;
  /**
   * `priceCents - netCents`. POSITIVE means the deal is priced ABOVE the sum of
   * its discounted packages. Null whenever there is no honest comparison to
   * make — never 0 standing in for "unknown".
   */
  deltaCents: number | null;
  headline: string;
  detail: string;
}

/**
 * SHOW THE GAP; DO NOT RESOLVE IT (spec §12/A2, this task's Step 4).
 *
 * The deal's price is what a redeeming tenant is quoted regardless of what its
 * packages add up to, so the two numbers are allowed to disagree — and when they
 * do, the operator has to be able to see it at a glance and in which direction.
 * Nothing here corrects either number.
 *
 * The three "no comparison" cases are kept distinct rather than collapsed into
 * one, because "there is no price yet", "this deal has no packages" and "these
 * packages cannot be priced" call for three different actions.
 */
export function dealPriceGap(
  priceCents: number | null | undefined,
  totals: DealPackagesTotals | null | undefined,
  packageCount: number,
): DealPriceGap {
  if (priceCents === null || priceCents === undefined) {
    return {
      kind: 'no-price',
      deltaCents: null,
      headline: 'No deal price yet',
      detail: 'Enter the monthly price this deal offers to compare it against its packages.',
    };
  }
  if (packageCount === 0) {
    return {
      kind: 'no-packages',
      deltaCents: null,
      headline: 'No software packages on this deal',
      detail: `There is nothing to compare ${money(priceCents)} against. The deal still prices at ${money(priceCents)} a month.`,
    };
  }
  if (!totals || totals.missingPackageIds.length > 0) {
    const missing = totals?.missingPackageIds || [];
    return {
      kind: 'unpriceable',
      deltaCents: null,
      headline: 'The packages cannot be totalled',
      detail: missing.length
        ? `${missing.length} package(s) on this deal have no entry in the packages catalog (${missing.join(', ')}), so the parts do not add up to a number worth comparing. This is a data gap, not a total of zero.`
        : 'The packages have not been priced, so there is nothing to compare against yet.',
    };
  }
  const deltaCents = priceCents - totals.netCents;
  if (deltaCents === 0) {
    return {
      kind: 'match',
      deltaCents: 0,
      headline: `Priced exactly at its parts — ${money(priceCents)}/mo`,
      detail: 'The deal price and the sum of its discounted packages agree to the cent.',
    };
  }
  const below = deltaCents < 0;
  return {
    kind: below ? 'below' : 'above',
    deltaCents,
    headline: `${money(Math.abs(deltaCents))}/mo ${below ? 'BELOW' : 'ABOVE'} the sum of its packages`,
    detail: `Its packages come to ${money(totals.netCents)}/mo after their own discounts; this deal offers ${money(priceCents)}/mo. The deal price stands on its own — nothing recalculates it from the packages.`,
  };
}

// ---------------------------------------------------------------------------
// TASK 4 — the recompose bar: the packages sum vs. the deal price, through
// the SAME `dealStrikethrough` the public landing page uses to decide
// whether to render a "was $X" strikethrough. This is a preview of that
// decision, not a second opinion of it.
// ---------------------------------------------------------------------------

export function recomposeBarState(
  listCents: number | null,
  priceCents: number | null,
): { tone: 'ok' | 'markup' | 'unavailable'; percentOff: number } {
  if (listCents == null || priceCents == null || priceCents <= 0) return { tone: 'unavailable', percentOff: 0 };
  const s = dealStrikethrough(listCents, priceCents);
  if (s.show) return { tone: 'ok', percentOff: s.percentOff };
  return { tone: 'markup', percentOff: 0 };
}

// ---------------------------------------------------------------------------
// Device packages (Step 3)
// ---------------------------------------------------------------------------

export interface DeviceOfferTotals {
  /** Device offers that bill EVERY month (installment, rental, lease). */
  monthlyCents: number;
  /** Device offers bought once, up front (outright). */
  oneTimeCents: number;
  /** Offers explicitly given away with the deal. */
  includedCount: number;
  /**
   * SKUs whose `saleMode` says nothing about cadence, so their amount belongs
   * in neither bucket. Listed rather than folded into one, because guessing
   * would make a monthly total quietly wrong.
   */
  uncounted: string[];
}

/**
 * What the hardware on this deal costs, split by cadence.
 *
 * DELIBERATELY SEPARATE FROM `priceCents`. A deal's price is its monthly
 * SOFTWARE price — `packagesTotals` covers software packages and nothing else,
 * on the server as much as here — and device finance is billed by its own SKU.
 * Adding them into one headline number would misstate both.
 */
export function deviceOfferTotals(skus: Pick<BillingSkuRow, 'skuId' | 'saleMode' | 'amountCents'>[]): DeviceOfferTotals {
  const out: DeviceOfferTotals = { monthlyCents: 0, oneTimeCents: 0, includedCount: 0, uncounted: [] };
  for (const sku of skus || []) {
    const amount = Math.max(0, Math.round(sku.amountCents || 0));
    switch (sku.saleMode) {
      case 'installment':
      case 'rental':
      case 'lease':
        out.monthlyCents += amount;
        break;
      case 'outright':
        out.oneTimeCents += amount;
        break;
      case 'included':
        out.includedCount += 1;
        break;
      default:
        out.uncounted.push(sku.skuId);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Device deal terms — the itemised preview rows
// ---------------------------------------------------------------------------

/**
 * ONE device line on the deal's preview, already tiered.
 *
 * The whole point of the "line by line" bill: a device is its own row with its
 * own name and its own money, never folded into a hardware subtotal. A device
 * whose deal covers only the first N units produces TWO rows — the covered
 * units and the rest at list — because that is what the customer is billed.
 */
export interface DeviceLineRow {
  skuId: string;
  name: string;
  saleMode?: string;
  quantity: number;
  /** Per unit. */
  monthlyCents: number;
  oneTimeCents: number;
  listMonthlyCents: number;
  listOneTimeCents: number;
  fromDeal: boolean;
  /** False when the operator chose to keep this line's one-time amount off the customer preview. */
  showOneTime: boolean;
  /**
   * WHAT IT COSTS DURING A LIMITED-TIME PRICE, and for how long. Equal to the
   * settled amounts with `promoMonths` absent when there is no promotion, so a
   * caller can read them unconditionally.
   */
  promoMonthlyCents: number;
  promoOneTimeCents: number;
  promoMonths?: number;
}

/**
 * The device rows a deal previews, one per tier.
 *
 * PREVIEWED AT ONE UNIT EACH, deliberately. A deal is a template — it does not
 * know how many terminals a tenant will buy, and inventing a quantity would put
 * a number on the operator's screen that no customer will ever be charged. The
 * `first_n` split therefore does not appear here; it appears in the WIZARD,
 * where a real quantity exists. What the operator sees here is the per-unit
 * offer, which is exactly what they are negotiating.
 */
export function deviceLineRows(
  skus: BillingSkuRow[],
  termsBySkuId: Record<string, DeviceTermInput> | undefined,
  /**
   * The unified offers, as typed. PREFERRED over `termsBySkuId` — the builder's
   * drawer writes here and nowhere else, and pricing off the legacy field left
   * every device row, scenario card and marketed total frozen at list while the
   * drawer's own outcome line moved. Same defect the packages had.
   */
  offers?: Record<string, OfferTermInput>,
): DeviceLineRow[] {
  const rows: DeviceLineRow[] = [];
  for (const sku of skus || []) {
    const priceable = {
      skuId: sku.skuId,
      amountCents: sku.amountCents,
      saleMode: sku.saleMode,
      ...(typeof (sku as any).downPaymentCents === 'number'
        ? { downPaymentCents: (sku as any).downPaymentCents }
        : {}),
    };

    const offerInput = offers?.[sku.skuId];
    if (offerInput) {
      const shape = deviceShape(sku.saleMode);
      // Only an offer that VALIDATES prices anything, exactly as the legacy
      // branch below treats a half-typed term.
      const term = offerTermError(offerInput, shape) ? undefined : toOfferTerm(offerInput, shape);
      const a = offerAmounts(deviceListPricing(priceable), term);
      rows.push({
        skuId: sku.skuId,
        name: sku.name,
        saleMode: sku.saleMode,
        quantity: 1,
        monthlyCents: a.settledMonthlyCents,
        oneTimeCents: a.settledOneTimeCents,
        listMonthlyCents: a.listMonthlyCents,
        listOneTimeCents: a.listOneTimeCents,
        fromDeal: a.priced,
        showOneTime: offerShowsOneTime(term),
        promoMonthlyCents: a.promoMonthlyCents,
        promoOneTimeCents: a.promoOneTimeCents,
        ...(a.promoMonths ? { promoMonths: a.promoMonths } : {}),
      });
      continue;
    }

    const input = termsBySkuId?.[sku.skuId];
    // Only a term that VALIDATES prices anything. A half-typed one leaves the
    // row at list rather than flickering to a number the operator cannot save.
    const term = deviceTermError(input) ? undefined : toDeviceTerm(input);
    const [tier] = deviceDealTiers({ sku: priceable, quantity: 1, term });
    if (!tier) continue;
    rows.push({
      skuId: sku.skuId,
      name: sku.name,
      saleMode: sku.saleMode,
      quantity: 1,
      monthlyCents: tier.monthlyCents,
      oneTimeCents: tier.oneTimeCents,
      listMonthlyCents: tier.listMonthlyCents,
      listOneTimeCents: tier.listOneTimeCents,
      fromDeal: tier.fromDeal && deviceTermIsPriced(term),
      showOneTime: showsOneTime(term),
      // The legacy shape has no promotion — that is exactly the capability
      // devices did not have before the unified offer.
      promoMonthlyCents: tier.monthlyCents,
      promoOneTimeCents: tier.oneTimeCents,
    });
  }
  return rows;
}

export interface DeviceDealTotals {
  /** Everything the devices add per month, deal-priced. */
  monthlyCents: number;
  /** Everything due once, deal-priced — INCLUDING amounts hidden from the preview. */
  oneTimeCents: number;
  /** The one-time amount the customer is actually SHOWN. Never larger than `oneTimeCents`. */
  visibleOneTimeCents: number;
  listMonthlyCents: number;
  listOneTimeCents: number;
  /** Devices given away outright by the deal or by their sale mode. */
  includedCount: number;
  /** SKUs whose `saleMode` says nothing about cadence, so their amount is in neither total. */
  uncounted: string[];
}

/**
 * What the devices on a deal cost, deal-priced, split by cadence.
 *
 * ⚠️ `oneTimeCents` ALWAYS includes amounts an operator hid from the preview.
 * Hiding is a marketing choice about one LINE; a total that quietly shrank to
 * match it would be a total the customer never agreed to and the invoice would
 * then exceed. `visibleOneTimeCents` is the separate, smaller figure for the
 * places that render the hidden state, and the difference between the two is
 * exactly what "it is still on the bill" means.
 */
export function deviceDealTotals(rows: DeviceLineRow[]): DeviceDealTotals {
  const out: DeviceDealTotals = {
    monthlyCents: 0, oneTimeCents: 0, visibleOneTimeCents: 0,
    listMonthlyCents: 0, listOneTimeCents: 0, includedCount: 0, uncounted: [],
  };
  for (const row of rows || []) {
    if (row.saleMode === 'included') { out.includedCount += 1; continue; }
    if (!row.saleMode) { out.uncounted.push(row.skuId); continue; }

    out.monthlyCents += row.monthlyCents * row.quantity;
    out.oneTimeCents += row.oneTimeCents * row.quantity;
    if (row.showOneTime) out.visibleOneTimeCents += row.oneTimeCents * row.quantity;
    out.listMonthlyCents += row.listMonthlyCents * row.quantity;
    out.listOneTimeCents += row.listOneTimeCents * row.quantity;

    // A device the deal discounted to nothing has been GIVEN away, and reads to
    // a customer exactly as an `included` one does.
    if (row.fromDeal && row.monthlyCents === 0 && row.oneTimeCents === 0) out.includedCount += 1;
  }
  return out;
}

/**
 * The headline a deal advertises: software + hardware per month, and what is
 * due today.
 *
 * The two halves stay SEPARATE in the return value rather than being summed
 * into one opaque figure, because the bill itemises them and `priceCents`
 * remains a SOFTWARE-only price on the server (`dealPricedPerPackage`
 * reconciles the deal's packages against it and knows nothing about hardware).
 * A caller that wants one number adds `softwareMonthlyCents` and
 * `deviceMonthlyCents`; nothing here pretends the deal's stored price already
 * includes hardware, because it does not.
 */
export interface DealHeadline {
  softwareMonthlyCents: number;
  deviceMonthlyCents: number;
  monthlyCents: number;
  /** Due today, everything included — hidden lines too. */
  dueTodayCents: number;
  /** Due today as the CUSTOMER sees it, after hidden lines are withheld. */
  visibleDueTodayCents: number;
  /** True when something is billed today that the preview is not showing. */
  hasHiddenDueToday: boolean;
}

// ---------------------------------------------------------------------------
// MARKETING SCENARIOS — the three ways one offer can be sold
// ---------------------------------------------------------------------------

/**
 * ⚠️ WHY A PLAIN SUM OF THE SELECTED DEVICES IS WRONG.
 *
 * One terminal offered outright, on 24-month instalments and on monthly rental
 * is THREE `device_finance` SKUs, and a deal that offers all three has all
 * three selected. But a customer takes exactly ONE of them. Adding them up —
 * which is what a single "devices monthly" total does — charges the deal for a
 * terminal three times over and produces a headline no customer will ever pay.
 *
 * So the offer is priced as SCENARIOS instead: one per finance mode actually on
 * the deal, each answering "what does this cost a customer who takes the
 * 24-month plan / rents / buys outright". That is the only framing an operator
 * can check a marketed price against, and the totals in each scenario are the
 * ones a real signup produces.
 *
 * A device that exists in only ONE mode (a printer sold outright and nothing
 * else) appears in EVERY scenario, at its only price — because a customer on
 * the rental plan still has to buy that printer. Its cost lands in the
 * scenario's `dueTodayCents`, never in the monthly, exactly as
 * `buildLocationBilling` bills it.
 */
export const FINANCE_MODE_LABELS: Record<string, string> = {
  installment: 'Financed monthly',
  rental: 'Rental',
  lease: 'Lease',
  outright: 'Buy outright',
  included: 'Included free',
};

/** The order scenarios are shown in — cheapest-monthly commitment first. */
const FINANCE_MODE_ORDER = ['installment', 'rental', 'lease', 'outright', 'included'];

export interface ScenarioDeviceRow {
  deviceName: string;
  skuId: string;
  skuName: string;
  saleMode: string;
  termMonths?: number;
  /** Per unit, as the deal prices it. */
  monthlyCents: number;
  oneTimeCents: number;
  listMonthlyCents: number;
  listOneTimeCents: number;
  fromDeal: boolean;
  showOneTime: boolean;
  /** The limited-time price and its length, carried from the device's own offer. */
  promoMonthlyCents: number;
  promoOneTimeCents: number;
  promoMonths?: number;
  /**
   * True when this device has NO option in the scenario's mode and is being
   * shown at the only price it has. Surfaced rather than hidden: an operator
   * checking the rental headline needs to know the printer in it is a purchase.
   */
  fallback: boolean;
}

export interface DealScenario {
  mode: string;
  label: string;
  rows: ScenarioDeviceRow[];
  softwareMonthlyCents: number;
  deviceMonthlyCents: number;
  /** Software + devices — the marketed monthly for a customer who takes this option. */
  monthlyCents: number;
  /** Everything due today, hidden lines included. */
  dueTodayCents: number;
  /** Due today as the CUSTOMER is shown it, after hidden lines are withheld. */
  visibleDueTodayCents: number;
  hasHiddenDueToday: boolean;
  /** List equivalents, for a "was" headline. */
  listMonthlyCents: number;
  /**
   * THE SCHEDULE this scenario actually bills on — software and this option's
   * hardware, through the one `buildPricePhases` the server schedules from.
   *
   * `monthlyCents` above is the SETTLED figure, and on an offer with a
   * limited-time price that is NOT what the customer pays first. Marketing the
   * settled number while charging a promotional one — or the reverse — is the
   * exact defect the phase model exists to prevent, so the first month and the
   * disclosure travel with the scenario rather than being re-derived by
   * whichever screen happens to render it.
   */
  phases: PricePhase[];
  /** What the customer pays in month one. Equal to `monthlyCents` when nothing is promoted. */
  firstMonthlyCents: number;
  /** True when the price changes at some point — i.e. there is something to disclose. */
  changes: boolean;
  /** The customer-facing sentence naming every phase, or null when the price never moves. */
  disclosure: string | null;
}

export interface ScenarioGroup {
  key: string;
  title: string;
  options: BillingSkuRow[];
}

/**
 * One scenario per finance mode the deal actually offers.
 *
 * Every price comes from `rowsBySkuId` — i.e. from `deviceLineRows`, i.e. from
 * `deviceDealTiers` — so a scenario cannot quote a number the server would not
 * bill. Nothing here discounts anything; it only chooses WHICH already-priced
 * option each device contributes to each scenario.
 *
 * A mode that no selected device offers produces no scenario, rather than an
 * empty card or a duplicate of another mode's numbers.
 */
export function dealScenarios(args: {
  groups: ScenarioGroup[];
  selectedSkuIds: string[];
  rowsBySkuId: Record<string, DeviceLineRow>;
  softwareMonthlyCents: number;
  /**
   * The software side as PHASED lines — `packagePhasedLines(packageRows)`.
   *
   * Optional, and when omitted the software is treated as one flat line at
   * `softwareMonthlyCents`, which is what every caller got before phases
   * existed. Passing it is what lets a scenario say "$5/mo for 12 months, then
   * $154.90" instead of quoting only the settled figure.
   */
  softwareLines?: PhasedLine[];
  /** Formats a phase total for the disclosure sentence. Defaults to plain dollars. */
  format?: (cents: number) => string;
}): DealScenario[] {
  const selected = new Set(args.selectedSkuIds || []);
  const software = Math.max(0, Math.round(args.softwareMonthlyCents || 0));

  // Only groups with at least one option ON the deal take part.
  const liveGroups = (args.groups || [])
    .map((g) => ({ ...g, chosen: g.options.filter((o) => selected.has(o.skuId)) }))
    .filter((g) => g.chosen.length > 0);

  const modesOffered = new Set<string>();
  for (const g of liveGroups) for (const o of g.chosen) if (o.saleMode) modesOffered.add(o.saleMode);

  const scenarios: DealScenario[] = [];
  for (const mode of FINANCE_MODE_ORDER) {
    if (!modesOffered.has(mode)) continue;

    const rows: ScenarioDeviceRow[] = [];
    for (const g of liveGroups) {
      // This device's option in THIS mode, or — when it has none — the option
      // it does have. A printer sold only outright is still bought by a
      // customer on the rental plan.
      const exact = g.chosen.find((o) => o.saleMode === mode);
      const pick = exact
        // Cheapest of whatever it does offer, so a fallback never quotes the
        // dearest option just because it was authored first.
        || g.chosen.slice().sort((a, b) => a.amountCents - b.amountCents)[0];
      if (!pick) continue;
      const row = args.rowsBySkuId[pick.skuId];
      if (!row) continue;
      rows.push({
        deviceName: g.title,
        skuId: pick.skuId,
        skuName: row.name,
        saleMode: pick.saleMode || '',
        ...(pick.termMonths ? { termMonths: pick.termMonths } : {}),
        monthlyCents: row.monthlyCents,
        oneTimeCents: row.oneTimeCents,
        listMonthlyCents: row.listMonthlyCents,
        listOneTimeCents: row.listOneTimeCents,
        fromDeal: row.fromDeal,
        showOneTime: row.showOneTime,
        promoMonthlyCents: row.promoMonthlyCents,
        promoOneTimeCents: row.promoOneTimeCents,
        ...(row.promoMonths ? { promoMonths: row.promoMonths } : {}),
        fallback: !exact,
      });
    }
    if (rows.length === 0) continue;

    let deviceMonthly = 0;
    let dueToday = 0;
    let visibleDueToday = 0;
    let listMonthly = 0;
    for (const r of rows) {
      deviceMonthly += r.monthlyCents;
      dueToday += r.oneTimeCents;
      if (r.showOneTime) visibleDueToday += r.oneTimeCents;
      listMonthly += r.listMonthlyCents;
    }

    // THE SCHEDULE — software lines plus this option's hardware, through the
    // same `buildPricePhases` the subscription is scheduled from. A device with
    // no promotion is still a phased line; including it is what makes a phase
    // total the WHOLE monthly bill rather than only the promoted part of it.
    const softwareLines: PhasedLine[] = args.softwareLines
      ?? (software > 0 ? [{ key: 'software', quantity: 1, standardAmountCents: software }] : []);
    const deviceLines: PhasedLine[] = rows.map((r) => ({
      key: r.skuId,
      quantity: 1,
      standardAmountCents: r.monthlyCents,
      ...(r.promoMonths && r.promoMonthlyCents !== r.monthlyCents
        ? { intro: { introPriceCents: r.promoMonthlyCents, introMonths: r.promoMonths } }
        : {}),
    }));
    const phases = buildPricePhases([...softwareLines, ...deviceLines]);
    const headline = phaseHeadline(phases);
    const format = args.format || ((cents: number) => `$${(cents / 100).toFixed(2)}`);

    scenarios.push({
      mode,
      label: FINANCE_MODE_LABELS[mode] || mode,
      rows,
      softwareMonthlyCents: software,
      deviceMonthlyCents: deviceMonthly,
      monthlyCents: software + deviceMonthly,
      dueTodayCents: dueToday,
      visibleDueTodayCents: visibleDueToday,
      hasHiddenDueToday: dueToday > visibleDueToday,
      listMonthlyCents: software + listMonthly,
      phases,
      // Falls back to the settled figure when there are no phases at all — a
      // scenario whose every line is free still markets at $0, not at nothing.
      firstMonthlyCents: phases.length ? headline.firstCents : software + deviceMonthly,
      changes: headline.changes,
      disclosure: phaseDisclosure(phases, format),
    });
  }
  return scenarios;
}

// ---------------------------------------------------------------------------
// THE THREE AUTO-PICKS — what a deals LIST shows without an operator opening it
// ---------------------------------------------------------------------------

/**
 * A deal sells one offer several ways, and a list row has space for one number.
 * Rather than pick arbitrarily (the deal's software `priceCents` alone says
 * nothing about the hardware a customer must also take), the row states the
 * three facts an operator actually compares deals on:
 *
 *   minMonthly  the lowest monthly commitment — and what it costs up front
 *   minDown     the least money due today — and what it costs per month
 *   best        the cheapest OVERALL, over the term the deal itself finances
 *
 * All three are `DealScenario`s straight out of `dealScenarios`, so the list
 * quotes the same figures the editor does. Nothing new is computed here; this
 * only chooses among what is already priced.
 */
export interface DealFinanceSummary {
  scenarios: DealScenario[];
  /** Lowest `monthlyCents`. Null when the deal offers no hardware at all. */
  minMonthly: DealScenario | null;
  /** Lowest `visibleDueTodayCents` — down payments included, via `deviceDealTiers`. */
  minDown: DealScenario | null;
  /** Lowest `visibleDueTodayCents + monthlyCents × bestTermMonths`. */
  best: DealScenario | null;
  /**
   * The horizon `best` was decided over, and it is SHOWN next to the badge
   * rather than left implicit. "Cheapest" is meaningless without one: a rental
   * with nothing due today wins at three months and loses badly at sixty, and
   * an operator who cannot see which horizon was used cannot check the claim.
   */
  bestTermMonths: number;
}

/**
 * The deal's own financed term, when it has one — the honest horizon to compare
 * over, because it is the period the deal itself asks a customer to commit to.
 * Falls back to 36 months, the usual life of POS hardware, when nothing on the
 * deal is financed for a fixed term.
 */
export const DEFAULT_COMPARISON_TERM_MONTHS = 36;

export function comparisonTermMonths(scenarios: DealScenario[]): number {
  let longest = 0;
  for (const s of scenarios) {
    for (const r of s.rows) {
      if (typeof r.termMonths === 'number' && r.termMonths > longest) longest = r.termMonths;
    }
  }
  return longest > 0 ? longest : DEFAULT_COMPARISON_TERM_MONTHS;
}

/** `visibleDueTodayCents + monthlyCents × months` — total cost of ownership as the CUSTOMER is shown it. */
export function scenarioTotalCents(s: DealScenario, months: number): number {
  return s.visibleDueTodayCents + s.monthlyCents * months;
}

/**
 * Group a deal's device SKUs the way a CUSTOMER picks them: one group per
 * physical device (a bundle, or a model), each holding every way to pay for it.
 *
 * Keyed `bundleId || modelId || skuId` — the SAME key the deal editor's
 * `deviceGroups` uses. A device offered outright, financed and rented is three
 * SKUs and ONE choice, and a grouping that missed that would let
 * `dealScenarios` charge for the same terminal three times.
 */
export function dealDeviceGroups(
  selectedSkuIds: string[],
  deviceSkus: BillingSkuRow[],
  nameFor?: (sku: BillingSkuRow) => string,
): ScenarioGroup[] {
  const selected = new Set(selectedSkuIds || []);
  const byKey = new Map<string, ScenarioGroup>();
  const order: string[] = [];

  for (const sku of deviceSkus || []) {
    if (!selected.has(sku.skuId)) continue;
    const key = sku.bundleId || sku.modelId || sku.skuId;
    let group = byKey.get(key);
    if (!group) {
      group = { key, title: nameFor?.(sku) || sku.name, options: [] };
      byKey.set(key, group);
      order.push(key);
    }
    group.options.push(sku);
  }
  return order.map((k) => byKey.get(k)!);
}

/**
 * THE WHOLE PRICING OF ONE DEAL, from the fields a deal (or a published deal
 * VERSION) carries.
 *
 * ⚠️ Feed this the ACTIVE PUBLISHED VERSION's fields wherever one exists. The
 * deal row is the working copy: its price may be an unpublished draft, and a
 * list that quoted it would advertise a number no merchant can sign. The deal
 * row is the right input for exactly one case — a deal that has never published
 * a version, which is its own implicit v1.
 */
export function dealFinanceSummary(args: {
  softwareMonthlyCents: number;
  devicePackageIds: string[];
  deviceTerms: Record<string, DeviceTermInput> | undefined;
  deviceSkus: BillingSkuRow[];
  nameFor?: (sku: BillingSkuRow) => string;
}): DealFinanceSummary {
  const selectedSkuIds = args.devicePackageIds || [];
  const selected = (args.deviceSkus || []).filter((s) => selectedSkuIds.includes(s.skuId));
  const rows = deviceLineRows(selected, args.deviceTerms);
  const scenarios = dealScenarios({
    groups: dealDeviceGroups(selectedSkuIds, args.deviceSkus, args.nameFor),
    selectedSkuIds,
    rowsBySkuId: Object.fromEntries(rows.map((r) => [r.skuId, r])),
    softwareMonthlyCents: args.softwareMonthlyCents,
  });

  if (scenarios.length === 0) {
    return { scenarios, minMonthly: null, minDown: null, best: null, bestTermMonths: DEFAULT_COMPARISON_TERM_MONTHS };
  }

  const bestTermMonths = comparisonTermMonths(scenarios);
  // `reduce` with a strict `<` keeps the FIRST winner on a tie, and
  // `dealScenarios` returns modes in cheapest-commitment-first order — so a tie
  // resolves to the lighter commitment rather than to whichever mode happened
  // to be authored first.
  const pick = (score: (s: DealScenario) => number) =>
    scenarios.reduce((a, b) => (score(b) < score(a) ? b : a));

  return {
    scenarios,
    minMonthly: pick((s) => s.monthlyCents),
    minDown: pick((s) => s.visibleDueTodayCents),
    best: pick((s) => scenarioTotalCents(s, bestTermMonths)),
    bestTermMonths,
  };
}

export function dealHeadline(softwareMonthlyCents: number, devices: DeviceDealTotals): DealHeadline {
  const software = Math.max(0, Math.round(softwareMonthlyCents || 0));
  return {
    softwareMonthlyCents: software,
    deviceMonthlyCents: devices.monthlyCents,
    monthlyCents: software + devices.monthlyCents,
    dueTodayCents: devices.oneTimeCents,
    visibleDueTodayCents: devices.visibleOneTimeCents,
    hasHiddenDueToday: devices.oneTimeCents > devices.visibleOneTimeCents,
  };
}

// ---------------------------------------------------------------------------
// Stripe price vs the deal's own price
// ---------------------------------------------------------------------------

export interface StripePriceLike {
  id: string;
  unitAmount: number;
  interval?: string;
  intervalCount?: number;
}

/**
 * A deal that ADVERTISES one number and CHARGES another.
 *
 * `platform-app`'s signup shows `deal.monthlyAmount` (which the API answers
 * from `priceCents` whenever it is set) and posts `deal.stripePriceId` as the
 * thing that gets charged. So a deal carrying both, with different amounts,
 * quotes a customer one price at the top of the page and bills them another at
 * the bottom. Nothing on the server refuses that combination; this is where an
 * operator finds out.
 */
export function stripePriceConflict(
  priceCents: number | null | undefined,
  price?: StripePriceLike | null,
): string | null {
  if (!price || priceCents === null || priceCents === undefined) return null;
  const every = stripeCadence(price);
  if (price.unitAmount !== priceCents) {
    return `Signup charges the Stripe price (${money(price.unitAmount)} per ${every}) but shows this deal’s price (${money(priceCents)} a month). A customer would be quoted ${money(priceCents)} and billed ${money(price.unitAmount)}. Make them match, or clear the Stripe price.`;
  }
  if ((price.interval || 'month') !== 'month' || (price.intervalCount ?? 1) !== 1) {
    return `The amounts match, but this Stripe price bills every ${every} while the deal price is stated per month. Pick a monthly Stripe price.`;
  }
  return null;
}

/** "month", "year", "3 months" — how often this Stripe price charges. */
export function stripeCadence(price: Pick<StripePriceLike, 'interval' | 'intervalCount'>): string {
  const unit = price.interval || 'month';
  return price.intervalCount && price.intervalCount > 1 ? `${price.intervalCount} ${unit}s` : unit;
}

/**
 * WHICH Stripe price a deal's `stripePriceId` actually refers to.
 *
 * `GET /admin/stripe/prices` lists `active: true`, `limit: 100`, recurring
 * only. A deal can perfectly well name a price that is none of those — one
 * archived since the deal was made, a one-off price, or the 101st. That id is
 * still what signup posts and still what the customer is charged; it is simply
 * absent from the list this screen can show.
 *
 * Modelled as three states rather than "found or null" because the third one is
 * the dangerous one: treated as null it disappears into "this deal has no
 * Stripe price", which is the opposite of the truth, and every comparison that
 * depends on the price switches itself off at the same time.
 */
export type StripePriceSelection =
  | { kind: 'none' }
  | { kind: 'listed'; price: StripePriceLike }
  | { kind: 'unlisted'; priceId: string };

export function stripePriceSelection(
  stripePriceId: string | null | undefined,
  prices: StripePriceLike[],
): StripePriceSelection {
  const id = (stripePriceId || '').trim();
  if (!id) return { kind: 'none' };
  const price = (prices || []).find((p) => p?.id === id);
  return price ? { kind: 'listed', price } : { kind: 'unlisted', priceId: id };
}

export type StripePriceNoticeKind = 'unlisted' | 'amount-conflict' | 'cadence-conflict';

export interface StripePriceNotice {
  kind: StripePriceNoticeKind;
  /**
   * Whether Save must be refused. TRUE only for a difference in AMOUNT: the
   * page has computed that a customer would be quoted one number and billed
   * another, and both remedies — match the amounts, or clear the Stripe price —
   * are in the same modal. An unlisted price and a cadence mismatch are told,
   * not blocked: neither is a number this screen can prove wrong, and refusing
   * to save would make an already-stored deal uneditable.
   */
  blocksSave: boolean;
  /** The full explanation, for the builder. */
  text: string;
  /** The same fact in a table cell's worth of words. */
  short: string;
}

/**
 * What must be said about the Stripe price on this deal — or null.
 *
 * ONE function for the builder and the deals table, so a row and the modal
 * cannot describe the same deal differently.
 */
export function stripePriceNotice(
  priceCents: number | null | undefined,
  selection: StripePriceSelection,
): StripePriceNotice | null {
  if (selection.kind === 'none') return null;

  if (selection.kind === 'unlisted') {
    const against = priceCents === null || priceCents === undefined
      ? 'the deal price'
      : `the ${money(priceCents)} above`;
    return {
      kind: 'unlisted',
      blocksSave: false,
      text: `This deal names Stripe price ${selection.priceId}, which is NOT among the active recurring prices Stripe returned — it may have been archived, may be a one-off price, or may simply be beyond the 100 this screen lists. Signup still charges it, so what a customer is billed cannot be checked against ${against}. Pick a listed price, or clear it and let the deal price stand on its own.`,
      short: 'Stripe price not listed — what it charges is unknown',
    };
  }

  const text = stripePriceConflict(priceCents, selection.price);
  if (!text) return null;
  if (priceCents !== null && priceCents !== undefined && selection.price.unitAmount !== priceCents) {
    return { kind: 'amount-conflict', blocksSave: true, text, short: 'quotes and charges differ' };
  }
  return {
    kind: 'cadence-conflict',
    blocksSave: false,
    text,
    short: `charged every ${stripeCadence(selection.price)}, quoted monthly`,
  };
}

/**
 * RETIRED (this task): a deal used to need a `stripePriceId` before it could go
 * on the landing page — signup hard-required one to charge. Pricing is now
 * package-base-prices → deal `priceCents` → composed subscription, and the
 * backend refusal this used to preview client-side is itself retired
 * (super-admin-ext commit 147e15f). Kept as a permanent no-op, not deleted,
 * so a stale caller importing it still compiles and still gets `null`.
 */
export function landingPageBlockReason(_form: {
  showOnLandingPage: boolean;
  priceCents: number | null;
  stripePriceId: string;
}): string | null {
  return null;
}

/** The server's own message, passed through untouched — there is no translation left to do. */
export function friendlyDealError(message?: string): string {
  return (message || '').trim() || 'Failed to save deal';
}

// ---------------------------------------------------------------------------
// The request body
// ---------------------------------------------------------------------------

export interface DealFormValues {
  name: string;
  code: string;
  stripePriceId: string;
  /**
   * Set when the operator REMOVES a legacy Stripe price.
   *
   * An empty `stripePriceId` is omitted from the body, and the API reads an
   * omitted field as "leave the stored value in force" — so blanking it alone
   * could never clear it. This flag is what makes the body send `''`, which is
   * the API's documented clear.
   */
  clearStripePrice?: boolean;
  /** The deal's monthly price AS TYPED, in dollars. Parsed to integer cents here. */
  priceDollars: string;
  trialDays: number;
  /**
   * THE RETIRED FLAT TRIO — `commissionMode` / `flatFee` / `percentageRate`.
   *
   * OPTIONAL, and ABSENT ON A NEW DEAL (2026-09-03). Nothing on the pay path
   * reads any of the three: billing-service resolves commission entirely
   * through a CommissionPlan (`pickPlan`) and contains no reference to them.
   * The blank form nonetheless defaulted `percentageRate: 20`, so every deal
   * created since plans shipped carries a dead field that READS LIKE A REAL
   * RATE — it is what made a deal look like it paid 20% while its plan paid
   * 2% of margin, and it cost real time to disprove.
   *
   * Still carried when a deal ALREADY has them, so an edit round-trips a
   * legacy value rather than silently blanking stored data (the additive rule
   * in CLAUDE.md). New deals simply never gain one.
   */
  commissionMode?: 'flat' | 'percentage' | 'both';
  /**
   * Dollars, as typed. Converted to integer cents in the body with
   * `Math.round`, which is a FIX rather than a restatement of what the old page
   * did: it multiplied unrounded, so a $19.99 fee was sent as
   * `1998.9999999999998`. Do not "restore" the bare multiplication.
   */
  flatFee?: number;
  percentageRate?: number;
  /**
   * The CommissionPlan this deal pays partners under (2026-08-29). '' = none,
   * and billing's cascade then falls through to the global default plan. Wins
   * over the flat trio above, which stays the fallback for deals built before
   * plans existed. See shared-types `deal.ts`.
   */
  commissionPlanId?: string;
  /**
   * RUBY ESTATE FEE PLAN (Task 34, mirrors `commissionPlanId`). Shown on the
   * editor only when the deal includes a package whose `productLine` is
   * `rubyestate` — see `DealEditPage`'s `dealIsEstate`. '' / undefined = no
   * plan named; the deal's business then resolves to the platform default
   * exactly as if these fields never existed (Task 33 `resolveFeePlan`).
   */
  estateFeePlanId?: string;
  /** Integer 1–60, or blank for "the life of the deal". See `estateFeePlanId`. */
  estateFeeMonths?: number;
  showOnLandingPage: boolean;
  /** Unlisted, but applicable by anyone who types the code — the ISO case. */
  redeemableByCode?: boolean;
  landingPageLabel: string;
  landingPageDescription: string;
  isDefaultDeal: boolean;
  isActive: boolean;
  maxUses?: number;
  expiresAt?: string;
  neverExpires: boolean;
  productId: string;
  /** The software packages this deal includes — what the discounts are attributed to. */
  packageIds: string[];
  packageDiscounts: Record<string, DiscountInput>;
  /**
   * `packageId -> an introductory price for that package`, as typed.
   *
   * Separate from `packageDiscounts` because they are different offers: a
   * discount changes the SETTLED price, an intro changes it for a fixed number
   * of months. Both may be set on one package.
   */
  packageIntros: Record<string, PriceIntroInput>;
  /**
   * THE UNIFIED OFFER MAP — `packageId | skuId -> the offer`, as typed.
   *
   * One map for software and hardware, because they are one concept. The
   * legacy `packageDiscounts` / `packageIntros` / `deviceTerms` above are still
   * seeded from storage so an old deal opens correctly, but everything the
   * builder WRITES goes through this.
   */
  offerTerms: Record<string, OfferTermInput>;
  deviceSkuIds: string[];
  /**
   * `skuId -> the deal's terms for that device finance option`, as typed.
   *
   * Keyed by SKU because a SKU IS the finance option: one terminal offered
   * outright, financed and rented is three SKUs and three independently
   * negotiable offers. Trimmed to `deviceSkuIds` on the way out, exactly as
   * `packageDiscounts` is trimmed to `packageIds`.
   */
  deviceTerms: Record<string, DeviceTermInput>;
  /**
   * LOCATION TERMS (2026-08-26 Plan & Packages spec) — REQUIRED on every
   * save. As-typed strings, parsed in `buildDealRequestBody` the same way
   * `priceDollars` is: how many locations the deal includes free, and the
   * monthly price (dollars) for each location beyond that quota.
   */
  freeLocationQuota: string;
  additionalLocationPriceDollars: string;
  /**
   * THE PAPERWORK THIS DEAL SELLS UNDER (2026-08-30) — hardware and software
   * agreements and policies, the return/refund policy, and operator-named
   * custom lines. Held on the form in the SAME shape it is stored and
   * validated in (`DealDocuments`), because unlike prices there is nothing to
   * parse: a document id is a document id on both sides.
   */
  documents: DealDocuments;
  /**
   * THE PARTNER'S OWN PAPERWORK for this deal (2026-08-30) — the commission
   * agreement they sign to be allowed to sell it, and the policies they are
   * held to. Deliberately NOT a third section inside `documents`: that object
   * is the CUSTOMER's binder and `dealDocumentsConfigured` switches checkout on
   * it, so a partner's contract placed in there would be handed to the merchant
   * at checkout and counted as one of their required signatures.
   */
  commissionDocuments: DealDocumentSectionConfig;
}

/** Board 3: picking a tier swaps ONE package id and carries that line's offer with it. Nothing else changes. */
export function swapDealTier<T extends { packageIds: string[]; packageDiscounts?: Record<string, any>; packageIntros?: Record<string, any>; offerTerms?: Record<string, any> }>(
  form: T, fromId: string, toId: string,
): T {
  if (fromId === toId) return form;
  const move = (m?: Record<string, any>) => {
    if (!m) return m;
    const { [fromId]: v, ...rest } = m;
    return v === undefined ? rest : { ...rest, [toId]: v };
  };
  return {
    ...form,
    packageIds: form.packageIds.map((id) => (id === fromId ? toId : id)),
    packageDiscounts: move(form.packageDiscounts),
    packageIntros: move(form.packageIntros),
    offerTerms: move(form.offerTerms),
  };
}

export interface DealRequestBody {
  name: string;
  code: string;
  /**
   * RETIRED (Task 2): there is no control left in the builder to set or clear
   * this. Present only when the deal already carried one, so an existing
   * legacy value round-trips unchanged — never sent as `''`, which is how the
   * removed picker's clear button used to work.
   */
  stripePriceId?: string;
  priceCents: number;
  monthlyAmount: number;
  trialDays: number;
  /** The retired flat trio — sent ONLY for a deal that already carries it.
   *  See DealFormValues; nothing on the pay path reads these. */
  commissionMode?: 'flat' | 'percentage' | 'both';
  flatFee?: number;
  percentageRate?: number;
  /** '' clears the deal's plan; see DealFormValues. */
  commissionPlanId: string;
  /** Sent only when set — see `DealFormValues.estateFeePlanId`. */
  estateFeePlanId?: string;
  estateFeeMonths?: number;
  showOnLandingPage: boolean;
  /**
   * Unlisted, but applicable by anyone who TYPES the code — the ISO case.
   * Always sent (never omitted) so turning listing on clears it: `updateDeal`
   * writes every field that is not `undefined`, and a stale `true` left on a
   * now-listed deal would be a permission nobody granted.
   */
  redeemableByCode: boolean;
  landingPageLabel: string;
  landingPageDescription: string;
  isDefaultDeal: boolean;
  isActive: boolean;
  /**
   * `null` means "no limit", and is NOT the same as omitting the key.
   * `updateDeal` writes every field whose value is `!== undefined`, so an
   * omitted key leaves whatever is stored in force — see `buildDealRequestBody`.
   */
  maxUses?: number | null;
  /** `null` means "never expires". Same reason as `maxUses`. */
  expiresAt?: number | null;
  productId: string;
  packageIds?: string[];
  packageDiscounts: Record<string, PlanDiscount>;
  /** Trimmed to the packages on the deal; entries with no offer are omitted. */
  packageIntros: Record<string, PriceIntro>;
  /** The unified offer map — supersedes the three above, which are still sent for compatibility. */
  offerTerms: Record<string, DealOfferTerm>;
  devicePackageIds: string[];
  deviceSkuIds: string[];
  /** Trimmed to the devices actually on the deal; entries that move no price are omitted. */
  deviceTerms: Record<string, DealDeviceTerm>;
  /** REQUIRED (2026-08-26): every deal defines its location economics. */
  freeLocationQuota: number;
  additionalLocationPriceCents: number;
  /**
   * ALWAYS SENT, `{}` included — for the same reason `commissionPlanId` is
   * always sent as a possibly-empty string. `updateDeal` writes every field
   * that is not `undefined` and has no remove branch, so an emptied binder has
   * to travel as an explicit `{}` or a deal's paperwork could never be cleared.
   */
  documents: DealDocuments;
  /** Always sent, `{}` included — see `documents` above for why. */
  commissionDocuments: DealDocumentSectionConfig;
}

/**
 * THE ONE PLACE A DEAL IS TURNED INTO A REQUEST — for create and for update
 * alike, so the two cannot drift.
 *
 * ⚠️ THE DEFECT THIS FIXES. The previous page sent `monthlyAmount` and NEVER
 * `priceCents`. Since `priceCents` now takes precedence on every read
 * (`effectiveMonthlyAmountCents`), repricing a builder-made deal wrote a value
 * that every subsequent response ignored — a save that appeared to succeed and
 * changed nothing. There is exactly one price field on the form now and it is
 * written to BOTH attributes:
 *
 *  - `priceCents` is the authoritative one (§12/A2);
 *  - `monthlyAmount` is deprecated but never removed, and is written with the
 *    IDENTICAL value so a consumer still reading it — and a super-admin-ext
 *    that predates `priceCents` — cannot see a different number. The server
 *    mirrors `priceCents` into it too; sending both means the row is right
 *    whichever half is in force.
 *
 * `devicePackageIds` and `deviceSkuIds` are sent for the same reason and with
 * the same value: `resolveDeviceSkuIds` prefers the new name and stores it
 * under the old one, and sending both EQUAL lists is a no-op under that rule
 * while still working against a build that only knows the old name. (Sending
 * them with DIFFERENT contents would not be a merge — the new name would
 * discard the old — so they are always derived from one array.)
 *
 * `packageDiscounts` is TRIMMED to the packages actually on the deal, mirroring
 * `buildPlanDraftBody`: a discount attributed to a package this deal does not
 * include explains nothing, and would keep being counted by nobody.
 */
/**
 * Normalises a form binder into what is stored, and names the first thing
 * wrong with it.
 *
 * COMPACTION IS NOT COSMETIC. `dealDocumentsConfigured` is the switch that
 * decides whether checkout reads the deal or falls back to the device SKU, and
 * it answers on the PRESENCE of lists rather than their contents. A binder
 * whose every list is empty must therefore compact all the way down to `{}`,
 * or an operator who cleared their last document would leave the deal claiming
 * to be papered while naming nothing — the one state that produces a checkout
 * with no agreement at all.
 *
 * Duplicate ids inside one list are dropped (they collapse in the resolver
 * anyway, and a picker should never have produced them), and lists are sorted
 * so two saves of the same binder produce byte-identical bodies — the same
 * rule `packageDiscounts` follows.
 */
export function compactDealDocuments(
  input: DealDocuments | undefined,
): { documents?: DealDocuments; error?: string } {
  const out: DealDocuments = {};

  for (const section of ['hardware', 'software'] as const) {
    const cfg = input?.[section];
    const agreements = Array.from(new Set(cfg?.agreementDocumentIds ?? [])).filter(Boolean).sort();
    const policies = Array.from(new Set(cfg?.policyDocumentIds ?? [])).filter(Boolean).sort();
    if (agreements.length || policies.length) {
      out[section] = { agreementDocumentIds: agreements, policyDocumentIds: policies };
    }
  }

  const refund = (input?.returnRefundPolicyDocumentId || '').trim();
  if (refund) out.returnRefundPolicyDocumentId = refund;

  const lines = input?.customLines ?? [];
  const kept: DealDocumentLine[] = [];
  const seenLineIds = new Set<string>();
  for (const line of lines) {
    const label = (line?.label || '').trim();
    const documentId = (line?.documentId || '').trim();
    // A half-filled row is the normal state of a line the operator just added
    // and has not finished — it is dropped, not an error, so an unfinished row
    // never blocks saving the rest of the deal.
    if (!label && !documentId) continue;
    if (!documentId) return { error: `Custom line "${label}": choose the document it points at.` };
    if (!label) return { error: 'Every custom line needs the name the customer will see at checkout.' };
    if (seenLineIds.has(line.lineId)) continue;
    seenLineIds.add(line.lineId);
    kept.push({ ...line, label, documentId });
  }
  if (kept.length) out.customLines = kept;

  return { documents: out };
}

export function buildDealRequestBody(
  form: DealFormValues,
  /**
   * The packages catalog, for resolving a PERCENTAGE software promotion into the
   * absolute `introPriceCents` the legacy `packageIntros` shape stores. Optional:
   * without it a percentage promotion simply writes no legacy mirror rather than
   * writing a wrong one — see "THE LEGACY MIRROR" below.
   */
  packages?: PackageRecordLike[],
): { body?: DealRequestBody; error?: string } {
  if (!form.productId) return { error: 'Select the product this deal sells before pricing it.' };
  if (!form.name.trim()) return { error: 'Give this deal a name.' };

  const parsed = dollarsToCents(form.priceDollars);
  if (parsed.error) return { error: `Deal price: ${parsed.error}` };
  const priceCents = parsed.cents!;

  // Location terms (2026-08-26): REQUIRED — createDeal refuses without them,
  // and any pricing edit through updateDeal must carry them, so the form
  // always sends both. Same parse posture as the deal price above.
  const quotaRaw = String(form.freeLocationQuota ?? '').trim();
  const quota = Number(quotaRaw);
  if (!quotaRaw || !Number.isInteger(quota) || quota < 0) {
    return { error: 'Free location quota: enter how many locations this deal includes free (0 or more).' };
  }
  const parsedLocationPrice = dollarsToCents(form.additionalLocationPriceDollars);
  if (parsedLocationPrice.error || parsedLocationPrice.cents === undefined || parsedLocationPrice.cents === null) {
    return { error: `Additional location price: ${parsedLocationPrice.error || 'enter the monthly price for each extra location.'}` };
  }
  const additionalLocationPriceCents = parsedLocationPrice.cents;

  const included = Array.from(new Set(form.packageIds || []));
  const packageDiscounts: Record<string, PlanDiscount> = {};
  for (const packageId of included.slice().sort()) {
    const input = form.packageDiscounts?.[packageId];
    const problem = discountInputError(input);
    if (problem) return { error: `Discount on ${packageId}: ${problem}` };
    const discount = toDiscount(input);
    if (discount) packageDiscounts[packageId] = discount;
  }

  // Introductory prices, trimmed to the deal's packages for the same reason
  // `packageDiscounts` is: an intro on a package the deal does not include
  // describes money that never moves.
  const packageIntros: Record<string, PriceIntro> = {};
  for (const packageId of included.slice().sort()) {
    const input = form.packageIntros?.[packageId];
    const problem = priceIntroError(input);
    if (problem) return { error: `Introductory price on ${packageId}: ${problem}` };
    const intro = toPriceIntro(input);
    if (intro) packageIntros[packageId] = intro;
  }

  const listByPackageId = packagePriceMap(packages || []);

  const devices = Array.from(new Set(form.deviceSkuIds || []));

  // THE UNIFIED OFFERS, for packages and devices alike, trimmed to what the
  // deal actually includes — the same rule every other per-item map follows.
  const offerTerms: Record<string, DealOfferTerm> = {};
  for (const packageId of included.slice().sort()) {
    const input = form.offerTerms?.[packageId];
    if (!input) continue;
    const problem = offerTermError(input, PACKAGE_SHAPE);
    if (problem) return { error: `Offer on ${packageId}: ${problem}` };
    const term = toOfferTerm(input, PACKAGE_SHAPE);
    if (term) offerTerms[packageId] = term;
  }
  for (const skuId of devices.slice().sort()) {
    const input = form.offerTerms?.[skuId];
    if (!input) continue;
    // The device's sale mode is not known here, so the permissive shape is
    // used for validation; `toOfferTerm` in the row already dropped anything
    // the real shape does not carry.
    const shape = { hasMonthly: true, hasOneTime: true, hasQuantity: true };
    const problem = offerTermError(input, shape);
    if (problem) return { error: `Offer on ${skuId}: ${problem}` };
    const term = toOfferTerm(input, shape);
    if (term) offerTerms[skuId] = term;
  }

  // TRIMMED to the devices actually on the deal, mirroring `packageDiscounts`
  // above: terms for a device this deal does not include describe money that
  // never moves, and the server trims them the same way on the way back out.
  // Sorted so two saves of the same terms produce byte-identical bodies.
  const deviceTerms: Record<string, DealDeviceTerm> = {};
  for (const skuId of devices.slice().sort()) {
    const input = form.deviceTerms?.[skuId];
    const problem = deviceTermError(input);
    if (problem) return { error: `Device terms on ${skuId}: ${problem}` };
    const term = toDeviceTerm(input);
    if (term) deviceTerms[skuId] = term;
  }

  /* -------------------------------------------------------------------------
   * THE LEGACY MIRROR — why the offer is written twice
   * -------------------------------------------------------------------------
   *
   * `offerTerms` is read by NOTHING on the server. `billing-service`'s
   * `resolveDeal` builds its `perPackage` from `deal.packageDiscounts` and its
   * device terms from `deal.deviceTerms`; `deviceQuoteTerms` reads
   * `deviceTerms[sku].intro`; `super-admin-ext`'s `packagesTotalsFor` calls
   * `dealPackagesTotals(deal.packageIds, deal.packageDiscounts, …)`. The
   * unified field is a browser-side shape that the money path has never learnt.
   *
   * WHAT THAT COST, before this:
   *
   *  - A DEVICE offer was never billed. `deviceTerms` was written from
   *    `form.deviceTerms`, which the drawer stopped writing to when it moved to
   *    the unified control — so the map saved EMPTY (or stale) and every device
   *    billed at catalog list, however the card was quoted. Hardware has no
   *    `priceCents` safety net; it prices through its own SKU and nothing else.
   *  - A PACKAGE discount was saved stale — the value seeded at load, not the
   *    one the operator had just typed. The customer's total was still right,
   *    because `dealPricedPerPackage` reconciles the composed net down to
   *    `priceCents` (which IS derived from the offers), but the per-package
   *    attribution the record carries was wrong, and so was the
   *    `packagesTotals` the admin API reports.
   *
   * So every legacy field is now DERIVED FROM THE OFFER rather than from the
   * dead form fields. The offer stays the source of truth and is still written;
   * these are its projection into the shapes the server already reads, which is
   * why this is a client change and not surgery on the money path.
   *
   * ⚠️ ONE GAP REMAINS AND IS NOT FIXABLE HERE: a SOFTWARE limited-time price
   * is stored (`packageIntros`) but not billed. `locationPricePhases` says so in
   * its own comment — `composeSubscription` produces one flat software net per
   * location and no introductory net, so a package sold at "$5 for 12 months"
   * composes flat. Device promotions DO bill, through
   * `deviceQuoteTerms(...).introMonthlyCents`. Fixing software promotions is a
   * billing-service change.
   */
  /*
   * ⚠️ ITERATED OVER EVERY PACKAGE THE FORM HOLDS AN OFFER INPUT FOR — NOT over
   * `Object.keys(offerTerms)`.
   *
   * `toOfferTerm` returns `undefined` for an offer that changes no price, so a
   * discount an operator CLEARED produces no entry in `offerTerms` at all.
   * Keying off that map would then skip the package entirely and leave the stale
   * legacy discount — seeded at load — in place: the operator removes a
   * discount, the card shows list, and billing keeps charging the old one. The
   * rule is "the form holds an offer for this key, so the offer decides",
   * including when what it decides is nothing.
   */
  for (const packageId of included) {
    if (!form.offerTerms?.[packageId]) continue;
    const term = offerTerms[packageId];
    if (term?.discount) packageDiscounts[packageId] = term.discount as PlanDiscount;
    else delete packageDiscounts[packageId];

    // `PriceIntro` is an ABSOLUTE price, so a percentage promotion has to be
    // resolved against the package's list price. Without the catalog it cannot
    // be, and a WRONG intro price is far worse than none: the offer still rides
    // on `offerTerms`, and the legacy mirror simply says nothing.
    const listCents = listByPackageId[packageId]?.basePriceCents;
    const introCents = term?.promo
      ? (typeof term.promo.fixedPriceCents === 'number'
        ? term.promo.fixedPriceCents
        // The promotion comes off the SETTLED price, not off list — the same
        // order `offerAmounts` applies them in, so the mirror and the quote
        // cannot arrive at two numbers.
        : (typeof listCents === 'number' && term.promo.discount
          ? applyDiscount(applyDiscount(listCents, term.discount), term.promo.discount)
          : undefined))
      : undefined;
    if (term?.promo && typeof introCents === 'number') {
      packageIntros[packageId] = { introPriceCents: introCents, introMonths: term.promo.months };
    } else {
      delete packageIntros[packageId];
    }
  }

  for (const skuId of devices) {
    // Same rule as the packages above: the form holding an offer for this SKU
    // is what makes the offer authoritative, so a CLEARED one erases the legacy
    // term rather than leaving it to bill.
    if (!form.offerTerms?.[skuId]) continue;
    const term = offerTerms[skuId];
    if (!term) { delete deviceTerms[skuId]; continue; }
    // Field for field, plus the promotion — `DealDeviceTerm` carries an `intro`
    // that takes EITHER an absolute price or a percentage, so unlike a package
    // this needs no catalog lookup and loses nothing in translation.
    deviceTerms[skuId] = {
      ...(term.discount ? { discount: term.discount } : {}),
      ...(term.discount ? { takenFrom: term.takenFrom || 'monthly' } : {}),
      ...(term.covers === 'first_n'
        ? { covers: 'first_n' as const, ...(typeof term.coveredUnits === 'number' ? { coveredUnits: term.coveredUnits } : {}) }
        : { covers: 'each' as const }),
      ...(typeof term.coveredLocations === 'number' ? { coveredLocations: term.coveredLocations } : {}),
      ...(typeof term.downPaymentCents === 'number' ? { downPaymentCents: term.downPaymentCents } : {}),
      ...(term.showOneTimeInPreview === false ? { showOneTimeInPreview: false } : {}),
      ...(term.promo
        ? {
          intro: {
            ...(typeof term.promo.fixedPriceCents === 'number'
              ? { priceCents: term.promo.fixedPriceCents }
              : { percentOff: term.promo.discount?.percentOff ?? 0 }),
            months: term.promo.months,
          },
        }
        : {}),
      ...(term.reason ? { reason: term.reason } : {}),
    } as DealDeviceTerm;
  }

  const compacted = compactDealDocuments(form.documents);
  if (compacted.error) return { error: compacted.error };

  // Ruby Estate fee plan (Task 34). `estateFeeMonths` is meaningless without
  // a named plan, and an out-of-range month count is refused the same way an
  // invalid discount is — before it ever reaches the server.
  if (form.estateFeeMonths !== undefined && !form.estateFeePlanId) {
    return { error: 'Ruby Estate fee plan: a month count needs a plan — choose one or clear the months.' };
  }
  if (form.estateFeeMonths !== undefined
    && (!Number.isInteger(form.estateFeeMonths) || form.estateFeeMonths < 1 || form.estateFeeMonths > 60)) {
    return { error: 'Ruby Estate fee plan: months must be a whole number from 1 to 60.' };
  }

  const body: DealRequestBody = {
    name: form.name,
    code: form.code,
    priceCents,
    monthlyAmount: priceCents,
    trialDays: form.trialDays,
    // ONLY for a deal that already carries them. A new deal is created with
    // no flat trio at all, so it cannot show a rate nothing will ever pay.
    ...(form.commissionMode !== undefined ? { commissionMode: form.commissionMode } : {}),
    ...(form.flatFee !== undefined ? { flatFee: Math.round(form.flatFee * 100) } : {}),
    ...(form.percentageRate !== undefined ? { percentageRate: form.percentageRate } : {}),
    // Always sent, '' included: an omitted key leaves whatever is stored in
    // force, so clearing the picker has to travel as an explicit empty value
    // or a plan could never be REMOVED from a deal.
    commissionPlanId: (form.commissionPlanId || '').trim(),
    // Sent ONLY when set — unlike commissionPlanId these never clear a stored
    // value with an explicit empty string; the deal editor's picker offers no
    // "remove" action yet, so this mirrors "field absent" rather than
    // inventing a clear semantics the brief does not ask for.
    ...(form.estateFeePlanId ? { estateFeePlanId: form.estateFeePlanId } : {}),
    ...(form.estateFeeMonths !== undefined ? { estateFeeMonths: form.estateFeeMonths } : {}),
    showOnLandingPage: form.showOnLandingPage,
    /* Only meaningful for an unlisted deal; a listed one is applicable by
       anyone already. Sent as a hard false when listed so turning listing ON
       cannot leave a stale redeemable flag behind on the row. */
    redeemableByCode: form.showOnLandingPage ? false : form.redeemableByCode === true,
    landingPageLabel: form.landingPageLabel,
    landingPageDescription: form.landingPageDescription,
    isDefaultDeal: form.isDefaultDeal,
    isActive: form.isActive,
    // NULL, NOT OMITTED — this is how the two are REMOVED.
    //
    // `updateDeal` writes every allowed field whose value is `!== undefined`,
    // and has no REMOVE branch. `JSON.stringify` drops an `undefined` value
    // entirely, so sending `undefined` for these was a save that told the
    // operator "Deal updated successfully" and left the stored expiry and use
    // limit exactly where they were: "Set to never expire" and clearing Max
    // Uses were silent no-ops.
    //
    // `null` travels, lands in the same `SET` the server already builds for
    // these two fields, and reads as absent everywhere they are consumed —
    // both the only server reader (`deal.expiresAt && …`, `deal.maxUses && …`
    // in `validateCode`) and this form's own seed (`neverExpires: !deal.expiresAt`)
    // test them for truthiness. No new API contract: the field is one the
    // endpoint already accepts, carrying a value its own comment already
    // documents the loop as writing.
    maxUses: form.maxUses ?? null,
    expiresAt: form.neverExpires
      ? null
      : (form.expiresAt ? new Date(form.expiresAt).getTime() : null),
    productId: form.productId,
    packageDiscounts,
    packageIntros,
    offerTerms,
    devicePackageIds: devices,
    deviceSkuIds: devices,
    deviceTerms,
    freeLocationQuota: quota,
    additionalLocationPriceCents,
    documents: compacted.documents || {},
    // Compacted the same way each `documents` section is: de-duplicated and
    // sorted so two saves of the same choice produce byte-identical bodies,
    // and always sent so an emptied list actually clears.
    commissionDocuments: {
      agreementDocumentIds: Array.from(new Set(form.commissionDocuments?.agreementDocumentIds ?? []))
        .filter(Boolean).sort(),
      policyDocumentIds: Array.from(new Set(form.commissionDocuments?.policyDocumentIds ?? []))
        .filter(Boolean).sort(),
    },
  };

  // Sent only when there is something to send. An EMPTY array would be stored
  // verbatim by `updateDeal` while `createDeal` treats it as "not provided" and
  // fills it from the product's defaults — two different outcomes for one
  // request. Omitting it keeps the pre-existing behaviour on both paths: the
  // product's `defaultPackages` decide, which is exactly what the builder shows
  // when a deal has no package list of its own.
  if (included.length) body.packageIds = included;

  // stripePriceId is a RETIRED field (Task 2): pricing is packages → priceCents
  // now, and there is no control in the builder to set or clear it any more.
  // Sent ONLY when the form already carries one — an existing legacy value
  // round-trips unchanged. Never sent as '': that used to be how the removed
  // picker's clear button worked, and sending it now would strip a legacy
  // value nobody asked to remove.
  if (form.stripePriceId) body.stripePriceId = form.stripePriceId;
  else if (form.clearStripePrice) body.stripePriceId = '';

  return { body };
}

/**
 * Final review C-I4: `buildDealRequestBody` OMITS an unset Estate fee plan / month count (right
 * for a create). On an UPDATE, omitting a field the saved deal still carries leaves it in place --
 * "undefined never clears a field". So for each of the two that the saved deal HAS and the form
 * has now cleared, send `null`; super-admin-ext REMOVEs the attribute.
 */
export function estateFeeClears(
  saved: { estateFeePlanId?: string; estateFeeMonths?: number } | null | undefined,
  form: { estateFeePlanId?: string; estateFeeMonths?: number },
): { estateFeePlanId?: null; estateFeeMonths?: null } {
  const out: { estateFeePlanId?: null; estateFeeMonths?: null } = {};
  if (!saved) return out;
  if (saved.estateFeePlanId && !form.estateFeePlanId) out.estateFeePlanId = null;
  if (saved.estateFeeMonths !== undefined && saved.estateFeeMonths !== null && form.estateFeeMonths === undefined) out.estateFeeMonths = null;
  return out;
}

/**
 * Small-fixes item 22: picking (or clearing) the Estate fee plan in the deal editor. Clearing the
 * plan clears the months with it -- a month count without a plan is refused by
 * `buildDealRequestBody`, so leaving the months behind would block Save. On an update,
 * `estateFeeClears` then sends `null` for both fields the saved deal still carries.
 */
export function withEstateFeePlan<T extends { estateFeePlanId?: string; estateFeeMonths?: number }>(form: T, planId: string | null | undefined): T {
  if (planId) return { ...form, estateFeePlanId: planId };
  return { ...form, estateFeePlanId: '', estateFeeMonths: undefined };
}

/**
 * Small-fixes item 24: the Estate fee fields render only when a package on the deal carries
 * `productLine: 'rubyestate'`. When the deal already HAS an Estate fee plan but no package says
 * so (e.g. the catalog rows came back without `productLine`), the fields would vanish silently --
 * this is the muted note shown instead. Never shown for a deal without an Estate plan, so a
 * Ruby Till deal looks exactly as before.
 */
export function estateFeeFieldsHiddenNote(dealIsEstate: boolean, estateFeePlanId: string | undefined): string | null {
  if (dealIsEstate || !estateFeePlanId) return null;
  return "This deal has a Ruby Estate fee plan, but none of its packages shows the Ruby Estate product line, so the fee fields are hidden. Check the packages' product line in Super Admin › Packages.";
}
